import type { TrackingPropsInterface } from '@marriott/mi-ui-library';
import { RESLINK } from '@marriott/mi-localstorage-utils';
import type { SlotContent, SlotTrackingContext } from '../../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';
import { isRecord } from '../helper';
import { EMPTY_RENDITIONS } from '../imageUtils';
import { trackingProps } from '../polarisTrackingUtils';
import {
  HOMEPAGE_RETARGETING_BADGE_ICON,
  HOMEPAGE_RETARGETING_BADGE_TEXT,
  HOMEPAGE_RETARGETING_DESCRIPTION,
  HOMEPAGE_HWS_RLM_USE_CASE,
} from './retargeting.constants';
import type { HomepageRetargetingCandidate, RetargetingProductDetailsCardProps } from './retargeting.types';

const VALID_CONTENT_TYPES = ['offers', 'property', 'destination', 'rnb'] as const;
const VALID_CARD_VARIATIONS = ['tall', 'square', 'wide'] as const;

export function normalizeSlotOrder(order: unknown): number | null {
  if (order === null || order === undefined) return null;
  const normalized = Number(order);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isValidRetargetingImageContext(value: unknown): boolean {
  if (!isRecord(value)) return false;
  if (!isNonEmptyString(value['candidateTimestamp']) || !isNonEmptyString(value['pageViewId'])) return false;
  if (normalizeSlotOrder(value['slotOrder']) === null) return false;
  if (value['suppliedImageUrl'] !== undefined && !isNonEmptyString(value['suppliedImageUrl'])) return false;

  if (value['useCase'] === HOMEPAGE_HWS_RLM_USE_CASE) return isNonEmptyString(value['marshaCode']);
  if (value['useCase'] === RESLINK) return value['marshaCode'] === undefined;
  return false;
}

export function isRetargetingPayload(value: unknown): value is RetargetingProductDetailsCardProps {
  if (!isRecord(value)) return false;

  return (
    VALID_CONTENT_TYPES.includes(value['contentType'] as (typeof VALID_CONTENT_TYPES)[number]) &&
    VALID_CARD_VARIATIONS.includes(value['cardVariation'] as (typeof VALID_CARD_VARIATIONS)[number]) &&
    isNonEmptyString(value['cardUrl']) &&
    typeof value['openInaNewTab'] === 'boolean' &&
    isNonEmptyString(value['title']) &&
    typeof value['description'] === 'string' &&
    typeof value['eyebrow'] === 'string' &&
    typeof value['imageAltText'] === 'string' &&
    isRecord(value['trackingProperties']) &&
    normalizeSlotOrder(value['index']) !== null &&
    isValidRetargetingImageContext(value['retargetingImageContext'])
  );
}

export function buildRetargetingProps(
  candidate: HomepageRetargetingCandidate,
  trackingCtx: SlotTrackingContext,
  pageViewId = 'standalone'
): RetargetingProductDetailsCardProps {
  const index = normalizeSlotOrder(trackingCtx.index) ?? 1;
  const cardVariation = trackingCtx.slotSize === 'banner' ? 'wide' : trackingCtx.slotSize;
  const content = {
    contentType: 'property',
    title: candidate.displayName,
  };
  const derivedTracking = trackingProps(content, { ...trackingCtx, index }, 'retargeting');
  const trackingProperties = {
    ...derivedTracking,
    clickTrack: false,
    impressionTrack: false,
    impressionCount: false,
    isDCAEnabled: false,
  } as TrackingPropsInterface;

  const retargetingImageContext =
    candidate.useCase === HOMEPAGE_HWS_RLM_USE_CASE
      ? {
          useCase: candidate.useCase,
          marshaCode: candidate.marshaCode,
          candidateTimestamp: candidate.timestamp,
          pageViewId,
          slotOrder: index,
          ...(candidate.imageUrl ? { suppliedImageUrl: candidate.imageUrl } : {}),
        }
      : {
          useCase: candidate.useCase,
          candidateTimestamp: candidate.timestamp,
          pageViewId,
          slotOrder: index,
          ...(candidate.imageUrl ? { suppliedImageUrl: candidate.imageUrl } : {}),
        };

  return {
    contentType: 'property',
    cardVariation,
    cardUrl: candidate.targetUrl,
    openInaNewTab: false,
    eyebrow: candidate.eyebrow,
    badgeText: HOMEPAGE_RETARGETING_BADGE_TEXT,
    badgeIcon: HOMEPAGE_RETARGETING_BADGE_ICON,
    title: candidate.displayName,
    description: HOMEPAGE_RETARGETING_DESCRIPTION,
    fileReferenceImage: candidate.imageUrl || undefined,
    retargetingImageContext,
    imageAltText: `${candidate.displayName} recently viewed property image`,
    dynamicMedia: EMPTY_RENDITIONS,
    dynamicMediaWide: EMPTY_RENDITIONS,
    dynamicMediaTall: EMPTY_RENDITIONS,
    trackingProperties,
    breakPointKey: 'viewportL',
    index,
  };
}

export function buildRetargetingCfFallbackSlot(slot: SlotContent): SlotContent | null {
  if (slot.cfFallback == null) return null;
  return { ...slot, type: 'cf', payload: slot.cfFallback, cfFallback: null };
}

export function buildRetargetingCandidateKey(props: RetargetingProductDetailsCardProps): string {
  const context = props.retargetingImageContext;
  return [
    context.useCase,
    context.useCase === HOMEPAGE_HWS_RLM_USE_CASE ? context.marshaCode : '',
    context.slotOrder,
    context.candidateTimestamp,
    context.suppliedImageUrl ?? '',
    context.pageViewId,
    props.cardUrl,
  ].join('|');
}

export function resolveRetargetingSlot(
  slot: SlotContent,
  candidate: HomepageRetargetingCandidate | null,
  trackingCtx: SlotTrackingContext,
  pageViewId: string
): SlotContent {
  const targetOrder = normalizeSlotOrder(slot.retargetingTarget?.order);
  if (targetOrder === null || targetOrder !== normalizeSlotOrder(slot.position)) return slot;
  if (!candidate) {
    return {
      ...slot,
      type: 'cf',
      payload: slot.cfFallback ?? slot.payload ?? null,
    };
  }

  return {
    ...slot,
    type: 'retargeting',
    payload: buildRetargetingProps(candidate, { ...trackingCtx, index: targetOrder }, pageViewId),
  };
}
